import {useState} from 'react'
import {getThreadPreviewsLineAndFileKeysFromThread} from '../../../../page-data/loaders/use-thread-previews'
import {ActionF, type ActionSeverity} from './ActionF'
import {CommentActionCollapsed} from './CommentActionCollapsed'
import {CommentActionExpanded} from './CommentActionExpanded'
import {CommentActionHeader} from './CommentActionHeader'
import type {Thread} from '@github-ui/diff-lines/conversation-types'
import {getCurrentUserLogin} from '@github-ui/client-env'

const calculateDiffStats = (diffLines?: Array<{type: string}>) => {
  if (!diffLines || diffLines.length === 0) {
    return {linesAdded: 0, linesDeleted: 0, linesChanged: 0}
  }
  let added = 0
  let deleted = 0
  for (const line of diffLines) {
    if (line.type === 'ADDITION') added++
    if (line.type === 'DELETION') deleted++
  }
  const changed = added + deleted
  return {linesAdded: added, linesDeleted: deleted, linesChanged: changed}
}

/**
 * Priority rules per the Actionability Framework:
 * - high: viewer is participating in thread AND thread is not resolved
 * - med: viewer is not participating, thread is not resolved
 * - low: thread is resolved OR viewer authored the thread (their own unresolved comment, waiting on others)
 */
export function getConversationPriority(thread: Thread, viewerLogin: string): ActionSeverity {
  if (thread.isResolved) return 'low'

  const comments = thread.commentsData?.comments ?? []
  const firstAuthor = comments[0]?.author?.login

  // Viewer opened the thread and no one else has replied yet → low
  if (firstAuthor === viewerLogin && comments.length === 1) return 'low'

  // Viewer is the last author → low
  const lastAuthor = comments[comments.length - 1]?.author?.login
  if (lastAuthor === viewerLogin) return 'low'

  const viewerParticipating = comments.some(c => c.author?.login === viewerLogin)

  // Viewer has commented → they're in the conversation, new reply needs attention
  if (viewerParticipating) return 'high'

  return 'med'
}

export const CommentAction = ({thread}: {thread: Thread}) => {
  const viewerLogin = getCurrentUserLogin() ?? ''
  const severity = getConversationPriority(thread, viewerLogin)

  const [isOpen, setIsOpen] = useState(false)
  const [startReplyExpanded, setStartReplyExpanded] = useState(false)
  const [isDismissed, setIsDismissed] = useState(false)

  const comments = thread.commentsData?.comments
  const commentIndex = comments ? comments.length - 1 : 0
  const comment = comments?.[commentIndex]

  const {filePath, lineKey} = getThreadPreviewsLineAndFileKeysFromThread(thread)

  const diffStats = calculateDiffStats(thread.subject?.diffLines)
  const firstDifflines = thread.subject?.diffLines?.slice(0, 2)

  if (!comment || isDismissed) return null

  const handleReply = () => {
    setStartReplyExpanded(true)
    setIsOpen(true)
  }

  const handleDismiss = () => setIsDismissed(true)

  // Auto-dismiss when viewer submits a reply
  const handleReplied = () => setIsDismissed(true)

  return (
    <ActionF
      key={comment.id}
      isOpen={isOpen}
      severity={severity}
      onToggle={open => {
        setIsOpen(open)
        if (!open) setStartReplyExpanded(false)
      }}
      header={
        <CommentActionHeader
          authorLogin={comment.author?.login}
          filePath={filePath}
          thread={thread}
          severity={severity}
        />
      }
      onDismiss={handleDismiss}
      collapsedContent={
        severity !== 'low' ? (
          <CommentActionCollapsed
            filePath={filePath}
            lineKey={lineKey}
            diffStats={diffStats}
            diffLines={firstDifflines}
            comment={comment}
            commentIndex={commentIndex}
            thread={thread}
            onReply={handleReply}
          />
        ) : undefined
      }
      expandedContent={
        <CommentActionExpanded
          filePath={filePath}
          lineKey={lineKey}
          diffStats={diffStats}
          thread={thread}
          startReplyExpanded={startReplyExpanded}
          onReplied={handleReplied}
        />
      }
    />
  )
}
